'use client';

/**
 * Form Components
 *
 * A set of form components that wrap the ShadCN form components.
 * Designed to work with react-hook-form for easy form management.
 */
import * as React from 'react';
import { DefaultValues, FieldValues, SubmitHandler, UseFormProps, UseFormReturn, useForm } from 'react-hook-form';

import { Button } from '@ui/components/common/button';
import {
  FormField,
  Form as ShadcnForm,
  FormControl as ShadcnFormControl,
  FormDescription as ShadcnFormDescription,
  FormItem as ShadcnFormItem,
  FormLabel as ShadcnFormLabel,
  FormMessage as ShadcnFormMessage,
  useFormField
} from '@ui/components/ui/form';
import { cn } from '@ui/lib/utils';

// eslint-disable-next-line react-refresh/only-export-components -- re-export form utilities for easier access
export { FormField, useFormField };

// Stable default objects to prevent re-renders
const DEFAULT_FORM_PROPS = {};
const DEFAULT_FOOTER_PROPS = {
  submitText: 'Submit',
  cancelText: 'Cancel',
  isSubmitting: false,
  showCancel: true
};

/**
 * Main Form component props
 */
export interface FormProps<TFormValues extends FieldValues = FieldValues> {
  /**
   * Form methods from react-hook-form
   */
  form: UseFormReturn<TFormValues>;

  /**
   * Form submission handler
   */
  onSubmit?: SubmitHandler<TFormValues>;

  /**
   * Additional CSS classes for the form element
   */
  className?: string;

  /**
   * Children elements
   */
  children: React.ReactNode;

  /**
   * HTML form attributes
   */
  formProps?: Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'>;
}

/**
 * Main Form component that wraps ShadCN Form
 */
export const Form = <TFormValues extends FieldValues = FieldValues>({
  form,
  onSubmit,
  className,
  children,
  formProps = DEFAULT_FORM_PROPS
}: FormProps<TFormValues>) => {
  return (
    <ShadcnForm {...form}>
      <form
        onSubmit={onSubmit ? form.handleSubmit(onSubmit) : undefined}
        className={cn('space-y-6', className)}
        {...formProps}
      >
        {children}
      </form>
    </ShadcnForm>
  );
};

/**
 * Form item component props
 */
export interface FormItemProps extends React.ComponentProps<typeof ShadcnFormItem> {
  /**
   * Children elements
   */
  children: React.ReactNode;
}

/**
 * FormItem component that wraps ShadCN FormItem
 */
export const FormItem = ({ children, className, ...props }: FormItemProps) => {
  return (
    <ShadcnFormItem className={cn('grid gap-2', className)} {...props}>
      {children}
    </ShadcnFormItem>
  );
};

/**
 * Form label component props
 */
export interface FormLabelProps extends React.ComponentProps<typeof ShadcnFormLabel> {
  /**
   * Label content
   */
  children: React.ReactNode;

  /**
   * Whether the field is required
   */
  required?: boolean;
}

/**
 * FormLabel component that wraps ShadCN FormLabel
 */
export const FormLabel = ({ children, required, className, ...props }: FormLabelProps) => {
  return (
    <ShadcnFormLabel className={cn('flex items-center gap-1', className)} {...props}>
      {children}
      {required && <span className="text-destructive ml-1">*</span>}
    </ShadcnFormLabel>
  );
};

/**
 * Form control component props
 */
export interface FormControlProps extends React.ComponentProps<typeof ShadcnFormControl> {
  /**
   * Children elements
   */
  children: React.ReactNode;
}

/**
 * FormControl component that wraps ShadCN FormControl
 */
export const FormControl = ({ children, ...props }: FormControlProps) => {
  return <ShadcnFormControl {...props}>{children}</ShadcnFormControl>;
};

/**
 * Form description component props
 */
export interface FormDescriptionProps extends React.ComponentProps<typeof ShadcnFormDescription> {
  /**
   * Description content
   */
  children: React.ReactNode;
}

/**
 * FormDescription component that wraps ShadCN FormDescription
 */
export const FormDescription = ({ children, className, ...props }: FormDescriptionProps) => {
  return (
    <ShadcnFormDescription className={cn('text-sm text-muted-foreground', className)} {...props}>
      {children}
    </ShadcnFormDescription>
  );
};

/**
 * Form message component props
 */
export interface FormMessageProps extends React.ComponentProps<typeof ShadcnFormMessage> {
  /**
   * Message content
   */
  children?: React.ReactNode;
}

/**
 * FormMessage component that wraps ShadCN FormMessage
 */
export const FormMessage = ({ children, className, ...props }: FormMessageProps) => {
  return (
    <ShadcnFormMessage className={cn('text-sm font-medium text-destructive', className)} {...props}>
      {children}
    </ShadcnFormMessage>
  );
};

/**
 * Form builder hook props
 */
export interface UseFormBuilderProps<TFormValues extends FieldValues = FieldValues> extends UseFormProps<TFormValues> {
  /**
   * Default values for the form
   */
  defaultValues: DefaultValues<TFormValues>;
}

/**
 * Hook to build a form with default values and options
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook exported alongside form components for convenience
export const useFormBuilder = <TFormValues extends FieldValues = FieldValues>({
  defaultValues,
  ...options
}: UseFormBuilderProps<TFormValues>) => {
  return useForm<TFormValues>({
    defaultValues,
    ...options
  });
};

/**
 * Form footer component props
 */
export interface FormFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Submit button text
   */
  submitText?: string;

  /**
   * Cancel button text
   */
  cancelText?: string;

  /**
   * Cancel button handler
   */
  onCancel?: () => void;

  /**
   * Whether the form is submitting
   */
  isSubmitting?: boolean;

  /**
   * Whether to show the cancel button
   */
  showCancel?: boolean;

  /**
   * Additional CSS classes for the container
   */
  className?: string;

  /**
   * Submit button props
   */
  submitButtonProps?: React.ComponentProps<typeof Button>;

  /**
   * Cancel button props
   */
  cancelButtonProps?: React.ComponentProps<typeof Button>;
}

/**
 * FormFooter component for form actions
 */
export const FormFooter = ({
  submitText = DEFAULT_FOOTER_PROPS.submitText,
  cancelText = DEFAULT_FOOTER_PROPS.cancelText,
  onCancel,
  isSubmitting = DEFAULT_FOOTER_PROPS.isSubmitting,
  showCancel = DEFAULT_FOOTER_PROPS.showCancel,
  className,
  submitButtonProps,
  cancelButtonProps,
  ...props
}: FormFooterProps) => {
  return (
    <div className={cn('flex items-center justify-end gap-4 pt-4', className)} {...props}>
      {showCancel && (
        <Button label={cancelText} type="button" variant="secondary" onClick={onCancel} {...cancelButtonProps} />
      )}
      <Button
        label={isSubmitting ? 'Submitting...' : submitText}
        type="submit"
        disabled={isSubmitting}
        variant="primary"
        {...submitButtonProps}
      />
    </div>
  );
};
